import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Heading, VStack, HStack, Input, Button, FormControl, FormLabel, Select, Spinner, useToast, Center,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, Flex, Divider, Text, Badge, useColorModeValue, Card, CardBody,
  Alert, AlertIcon, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, 
  useDisclosure, InputGroup, InputLeftElement, SimpleGrid, Container, Stack, Tooltip, Image, Avatar
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, AddIcon, WarningIcon, InfoIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';
import { FiFilter, FiRefreshCw, FiList, FiPlus, FiAlertCircle } from 'react-icons/fi';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const SubCategoriesPage = () => {
  // Theme colors
  const bgMain = useColorModeValue('gray.50', 'gray.800');
  const bgCard = "#fff8e1"; // Updated to light gold
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const themeColor = "#4bedffff";
  const themeTextColor = "black";
  
  // States
  const [subCategorias, setSubCategorias] = useState([]);
  const [filteredSubCategorias, setFilteredSubCategorias] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [nome, setNome] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [editId, setEditId] = useState(null);
  const [relatedIds, setRelatedIds] = useState([]);
  const [descricao, setDescricao] = useState('');
  const [palavrasChave, setPalavrasChave] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCategoriasLoading, setIsCategoriasLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  
  // Modal disclosure hooks
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isFormModalOpen, onOpen: onFormModalOpen, onClose: onFormModalClose } = useDisclosure();
  
  // State for subcategory to delete
  const [subCategoryToDelete, setSubCategoryToDelete] = useState(null);

  const toast = useToast();

  // Fetch categories from API
  const fetchCategorias = useCallback(async () => {
    setIsCategoriasLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/categories`);
      const data = await response.json();
      setCategorias(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar categorias',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCategoriasLoading(false);
    }
  }, [toast]);

  // Fetch subcategories from API
  const fetchSubCategorias = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/subcategories`);
      const data = await response.json();
      setSubCategorias(data);
      setFilteredSubCategorias(data);
    } catch (error) {
      console.error('Failed to fetch subcategories:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar subcategorias',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Load data on component mount
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchCategorias();
    fetchSubCategorias();
  }, []);

  // Filter subcategories when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSubCategorias(subCategorias);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = subCategorias.filter(
        (subCategoria) => 
          subCategoria.name.toLowerCase().includes(lowercasedSearch) ||
          (subCategoria.category?.name && 
           subCategoria.category.name.toLowerCase().includes(lowercasedSearch))
      );
      setFilteredSubCategorias(filtered);
    }
  }, [subCategorias, searchTerm]);

  // Handle form submission (create/update)
  const handleSubmit = async () => {
    if (!nome || !categoriaId) {
      toast({
        title: 'Erro',
        description: 'Por favor, forneça tanto o nome da subcategoria quanto selecione uma categoria',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const method = editId ? 'PUT' : 'POST';
      const url = editId ? `${API_BASE_URL}api/subcategories/${editId}` : `${API_BASE_URL}api/subcategories`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: nome,
          description: descricao,
          keywords: palavrasChave.split(',').map(k => k.trim()).filter(k => k),
          categoryId: categoriaId,
          relatedSubcategories: relatedIds,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (editId) {
          setSubCategorias(subCategorias.map(sub => (sub._id === editId ? data : sub)));
          toast({
            title: 'Sucesso',
            description: 'Subcategoria atualizada com sucesso',
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
          onFormModalClose();
        } else {
          setSubCategorias([...subCategorias, data]);
          toast({
            title: 'Sucesso',
            description: 'Subcategoria adicionada com sucesso',
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
          onFormModalClose();
        }

        setNome('');
        setDescricao('');
        setPalavrasChave('');
        setCategoriaId('');
        setRelatedIds([]);
        setEditId(null);
      } else {
        console.error('Failed to save subcategory:', data.error);
        toast({
          title: 'Erro',
          description: data.error || 'Falha ao salvar a subcategoria',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error during subcategory submission:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao salvar a subcategoria',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  // Set up form for editing
  const handleEdit = (subCategoria) => {
    setNome(subCategoria.name);
    setDescricao(subCategoria.description || '');
    setPalavrasChave(subCategoria.keywords ? subCategoria.keywords.join(', ') : '');
    setCategoriaId(subCategoria.category._id);
    setRelatedIds(subCategoria.relatedSubcategories ? subCategoria.relatedSubcategories.map(r => r._id || r) : []);
    setEditId(subCategoria._id);
    onFormModalOpen();
  };

  // Handle subcategory deletion
  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/subcategories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSubCategorias(subCategorias.filter(sub => sub._id !== id));
        toast({
          title: 'Sucesso',
          description: 'Subcategoria excluída com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        console.error('Failed to delete subcategory');
        toast({
          title: 'Erro',
          description: 'Falha ao excluir a subcategoria',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error during subcategory deletion:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao excluir a subcategoria',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
      onDeleteModalClose();
    }
  };

  // Get category name by ID
  const getCategoriaName = (category) => {
    if (!category || typeof category !== 'object') {
      return 'Categoria não encontrada';
    }    
    const categoria = categorias.find(cat => cat._id === category._id);
    return categoria ? categoria.name : 'Categoria não encontrada';
  };

  // Start new subcategory form
  const handleAddNew = () => {
    setEditId(null);
    setNome('');
    setDescricao('');
    setPalavrasChave('');
    setCategoriaId('');
    setRelatedIds([]);
    onFormModalOpen();
  };

  // Prepare deletion confirmation
  const confirmDelete = (id, name) => {
    setSubCategoryToDelete({ id, name });
    onDeleteModalOpen();
  };

  // Render loading state
  if (loading && !subCategorias.length) {
    return (
      <Container maxW="container.xl" p={6}>
        <Card shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
          <CardBody>
            <Flex justify="center" align="center" h="200px">
              <Spinner size="xl" thickness="4px" color={themeColor} />
              <Text ml={4} fontWeight="medium" color="black">Carregando subcategorias...</Text>
            </Flex>
          </CardBody>
        </Card>
      </Container>
    );
  }

  // Main component render
  return (
    <Container maxW="container.xl" p={6}>
      {/* Header */}
      <Card mb={6} shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
        <CardBody>
          <Flex justify="space-between" align="center" wrap="wrap">
            <HStack>
              <Heading as="h1" fontSize="2xl" fontWeight="bold" color="black">
                Subcategorias
              </Heading>
              <Badge bg={themeColor} color={themeTextColor} borderRadius="full" px={3} py={1}>
                {subCategorias.length} subcategoria{subCategorias.length !== 1 ? 's' : ''}
              </Badge>
            </HStack>
            
            <HStack spacing={3}>
              <Button
                leftIcon={<FiRefreshCw />}
                size="sm"
                variant="outline"
                borderColor={themeColor}
                color="black"
                onClick={fetchSubCategorias}
                isLoading={loading}
              >
                Atualizar
              </Button>
              
              <Button
                leftIcon={<FiPlus />}
                bg={themeColor}
                color={themeTextColor}
                onClick={handleAddNew}
                isDisabled={categorias.length === 0}
                _hover={{ bg: "#e5c100" }}
              >
                Nova Subcategoria
              </Button>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Search and Filter Bar */}
      <Card mb={6} shadow="sm" borderRadius="lg" bg={bgCard}>
        <CardBody>
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <HStack flexGrow={1} maxW={{ base: "100%", md: "320px" }}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar subcategorias..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  focusBorderColor={themeColor}
                  bg="white"
                />
              </InputGroup>
            </HStack>
            
            <HStack spacing={3}>
              <HStack spacing={2}>
                <IconButton
                  icon={<FiList />}
                  aria-label="Lista"
                  bg={viewMode === 'list' ? themeColor : 'gray.100'}
                  color={viewMode === 'list' ? themeTextColor : 'gray.500'}
                  onClick={() => setViewMode('list')}
                  borderRadius="md"
                />
                <IconButton
                  icon={<AddIcon />}
                  aria-label="Grid"
                  bg={viewMode === 'grid' ? themeColor : 'gray.100'}
                  color={viewMode === 'grid' ? themeTextColor : 'gray.500'}
                  onClick={() => setViewMode('grid')}
                  borderRadius="md"
                />
              </HStack>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Content Area */}
      {loading && subCategorias.length > 0 ? (
        <Card shadow="md" borderRadius="lg" bg={bgCard}>
          <CardBody>
            <Center py={10}>
              <Spinner size="xl" color={themeColor} thickness="4px" />
            </Center>
          </CardBody>
        </Card>
      ) : filteredSubCategorias.length === 0 ? (
        <Card shadow="md" borderRadius="lg" bg={bgCard}>
          <CardBody>
            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              {searchTerm 
                ? 'Nenhuma subcategoria encontrada com os termos de busca aplicados' 
                : 'Nenhuma subcategoria encontrada. Adicione sua primeira subcategoria usando o botão acima.'}
            </Alert>
          </CardBody>
        </Card>
      ) : viewMode === 'list' ? (
        // List View
        <Card shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
          <Box overflowX="auto">
            <Table colorScheme="yellow" size="md">
              <Thead bg={themeColor}>
                <Tr>
                  <Th color={themeTextColor}>Nome</Th>
                  <Th color={themeTextColor}>Categoria Principal</Th>
                  <Th color={themeTextColor}>Relacionadas</Th>
                  <Th width="130px" textAlign="center" color={themeTextColor}>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredSubCategorias.map((subCategoria) => (
                  <Tr key={subCategoria._id} _hover={{ bg: 'gray.50' }} transition="all 0.2s">
                    <Td fontWeight="medium" color="black">{subCategoria.name}</Td>
                    <Td>
                      <Badge colorScheme="blue" borderRadius="full" px={2} py={0.5}>
                        {getCategoriaName(subCategoria.category)}
                      </Badge>
                    </Td>
                    <Td>
                      {subCategoria.relatedSubcategories && subCategoria.relatedSubcategories.length > 0 ? (
                        <Flex gap={1} flexWrap="wrap">
                          {subCategoria.relatedSubcategories.map(r => (
                            <Badge key={r._id || r} colorScheme="green" borderRadius="full" px={2} py={0.5} fontSize="xs">
                              {r.name || r}
                            </Badge>
                          ))}
                        </Flex>
                      ) : (
                        <Text fontSize="sm" color="gray.400">—</Text>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={2} justify="center">
                        <Tooltip label="Editar Subcategoria" placement="top">
                          <IconButton
                            icon={<EditIcon />}
                            aria-label="Editar Subcategoria"
                            bg={themeColor}
                            color={themeTextColor}
                            size="sm"
                            onClick={() => handleEdit(subCategoria)}
                            _hover={{ bg: "#e5c100" }}
                          />
                        </Tooltip>
                        <Tooltip label="Excluir Subcategoria" placement="top">
                          <IconButton
                            icon={<DeleteIcon />}
                            aria-label="Excluir Subcategoria"
                            bg={themeColor}
                            color={themeTextColor}
                            size="sm"
                            onClick={() => confirmDelete(subCategoria._id, subCategoria.name)}
                            _hover={{ bg: "#e5c100" }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      ) : (
        // Grid View
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={6}>
          {filteredSubCategorias.map((subCategoria) => (
            <Card 
              key={subCategoria._id} 
              shadow="md" 
              borderRadius="lg" 
              overflow="hidden"
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-5px)', shadow: 'lg' }}
              bg={bgCard}
            >
              <Box bg={themeColor} h="8px" w="100%" />
              <CardBody p={4}>
                <Flex direction="column" h="100%">
                  <Heading size="md" mb={2} noOfLines={1} color="black">
                    {subCategoria.name}
                  </Heading>
                  <Text fontSize="sm" color="gray.500" mb={3}>
                    Categoria: 
                    <Badge ml={2} colorScheme="blue" borderRadius="full">
                      {getCategoriaName(subCategoria.category)}
                    </Badge>
                  </Text>
                  <Flex mt="auto" justify="flex-end" pt={3}>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<EditIcon />}
                        aria-label="Editar Subcategoria"
                        bg={themeColor}
                        color={themeTextColor}
                        size="sm"
                        onClick={() => handleEdit(subCategoria)}
                        _hover={{ bg: "#e5c100" }}
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        aria-label="Excluir Subcategoria"
                        bg={themeColor}
                        color={themeTextColor}
                        size="sm"
                        onClick={() => confirmDelete(subCategoria._id, subCategoria.name)}
                        _hover={{ bg: "#e5c100" }}
                      />
                    </HStack>
                  </Flex>
                </Flex>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Add/Edit Form Modal */}
      <Modal isOpen={isFormModalOpen} onClose={onFormModalClose} size="md">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="lg" bg={bgCard}>
          <Box bg={themeColor} h="8px" w="100%" />
          <ModalHeader color="black">
            {editId ? "Editar Subcategoria" : "Nova Subcategoria"}
          </ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <FormControl id="nome" isRequired>
                <FormLabel fontWeight="medium" color="black">Nome da Subcategoria</FormLabel>
                <Input
                  placeholder="Digite o nome da subcategoria"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  focusBorderColor={themeColor}
                  bg="white"
                />
              </FormControl>

              <FormControl id="descricao">
                <FormLabel fontWeight="medium" color="black">Descrição</FormLabel>
                <Input
                  as="textarea"
                  placeholder="Descreva brevemente esta subcategoria"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  focusBorderColor={themeColor}
                  bg="white"
                  minH="80px"
                  py={2}
                />
              </FormControl>

              <FormControl id="palavrasChave">
                <FormLabel fontWeight="medium" color="black">Palavras-chave</FormLabel>
                <Input
                  placeholder="Ex: encanador, vazamento, torneira (separadas por vírgula)"
                  value={palavrasChave}
                  onChange={(e) => setPalavrasChave(e.target.value)}
                  focusBorderColor={themeColor}
                  bg="white"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Separe as palavras-chave com vírgula. Essas palavras ajudam na busca inteligente.
                </Text>
              </FormControl>

              <FormControl id="categoriaId" isRequired>
                <FormLabel fontWeight="medium" color="black">Categoria Principal</FormLabel>
                <Select
                  placeholder="Selecione a categoria principal"
                  value={categoriaId}
                  onChange={(e) => { setCategoriaId(e.target.value); setRelatedIds([]); }}
                  focusBorderColor={themeColor}
                  bg="white"
                >
                  {categorias.map((categoria) => (
                    <option key={categoria._id} value={categoria._id}>
                      {categoria.name}
                    </option>
                  ))}
                </Select>
                {categorias.length === 0 && (
                  <Alert status="warning" borderRadius="md" mt={2} size="sm">
                    <AlertIcon />
                    <Text fontSize="sm">
                      Nenhuma categoria disponível. Adicione categorias primeiro.
                    </Text>
                  </Alert>
                )}
              </FormControl>

              {/* Related Subcategories - only show when a category is selected */}
              {categoriaId && (
                <FormControl id="relatedSubcategories">
                  <FormLabel fontWeight="medium" color="black">Subcategorias Relacionadas</FormLabel>
                  <Box maxH="200px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md" p={2} bg="white">
                    {subCategorias
                      .filter(s => s.category && (s.category._id || s.category) === categoriaId && s._id !== editId)
                      .map(s => (
                        <Flex key={s._id} align="center" py={1} px={2} _hover={{ bg: "gray.50" }} borderRadius="sm">
                          <input
                            type="checkbox"
                            checked={relatedIds.includes(s._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRelatedIds([...relatedIds, s._id]);
                              } else {
                                setRelatedIds(relatedIds.filter(id => id !== s._id));
                              }
                            }}
                            style={{ marginRight: '8px' }}
                          />
                          <Text fontSize="sm">{s.name}</Text>
                        </Flex>
                      ))}
                    {subCategorias.filter(s => s.category && (s.category._id || s.category) === categoriaId && s._id !== editId).length === 0 && (
                      <Text fontSize="sm" color="gray.500" p={2}>Nenhuma outra subcategoria nesta categoria.</Text>
                    )}
                  </Box>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            <Button variant="ghost" mr={3} onClick={onFormModalClose}>
              Cancelar
            </Button>
            <Button
              bg={themeColor}
              color={themeTextColor}
              onClick={handleSubmit}
              isLoading={loading}
              _hover={{ bg: "#e5c100" }}
              isDisabled={categorias.length === 0}
            >
              {editId ? 'Atualizar' : 'Adicionar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Edit Confirmation Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="sm">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="lg" bg={bgCard}>
          <Box bg={themeColor} h="8px" w="100%" />
          <ModalHeader color="black">
            Confirmar Atualização
          </ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody py={6}>
            <Flex alignItems="center">
              <InfoIcon color={themeColor} boxSize={5} mr={3} />
              <Text color="black">Tem certeza que deseja atualizar esta subcategoria?</Text>
            </Flex>
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            <Button variant="ghost" mr={3} onClick={onEditModalClose}>
              Cancelar
            </Button>
            <Button
              bg={themeColor}
              color={themeTextColor}
              onClick={handleSubmit}
              isLoading={loading}
              _hover={{ bg: "#e5c100" }}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} size="sm">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="lg" bg={bgCard}>
          <Box bg={themeColor} h="8px" w="100%" />
          <ModalHeader color="black">
            Confirmar Exclusão
          </ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody py={6}>
            <Flex alignItems="center">
              <WarningIcon color={themeColor} boxSize={5} mr={3} />
              <Text color="black">
                Tem certeza que deseja excluir a subcategoria <strong>{subCategoryToDelete?.name}</strong>? Esta ação não poderá ser desfeita.
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
              Cancelar
            </Button>
            <Button
              bg={themeColor}
              color={themeTextColor}
              onClick={() => {
                if (subCategoryToDelete) {
                  handleDelete(subCategoryToDelete.id);
                }
              }}
              isLoading={loading}
              _hover={{ bg: "#e5c100" }}
            >
              Excluir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default SubCategoriesPage;